import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Zap, CreditCard, TrendingUp } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { ReactNode } from 'react';

interface MetricCardProps {
  icon: ReactNode;
  count: number | null;
  label: string;
  href: string;
  loading: boolean;
}

function MetricCard({ icon, count, label, href, loading }: MetricCardProps) {
  return (
    <Link
      to={href}
      className="flex items-center gap-4 rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
        {icon}
      </div>
      <div>
        {loading ? (
          <div className="mb-1 h-8 w-12 animate-pulse rounded bg-gray-200" />
        ) : (
          <p className="text-3xl font-bold text-text-primary">{count ?? 0}</p>
        )}
        <p className="text-sm text-text-muted">{label}</p>
      </div>
    </Link>
  );
}

export function DashboardPage() {
  const [delivering, setDelivering] = useState<number | null>(null);
  const [awaitingGeneration, setAwaitingGeneration] = useState<number | null>(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState<number | null>(null);
  const [ordersThisMonth, setOrdersThisMonth] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMetrics() {
      const now = new Date();
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [deliveringRes, paidRes, subsRes, monthRes] = await Promise.all([
        // Currently delivering
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .in('status', ['approved', 'delivering']),

        // Awaiting generation
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('status', 'paid'),

        // Active subscriptions
        supabase
          .from('subscriptions')
          .select('id', { count: 'exact', head: true })
          .in('status', ['active', 'trialing']),

        // Orders this month
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', firstOfMonth),
      ]);

      setDelivering(deliveringRes.count ?? 0);
      setAwaitingGeneration(paidRes.count ?? 0);
      setActiveSubscriptions(subsRes.count ?? 0);
      setOrdersThisMonth(monthRes.count ?? 0);
      setLoading(false);
    }

    fetchMetrics();
  }, []);

  return (
    <div className="p-6">
      <h1 className="mb-6 font-heading text-2xl text-text-primary">Dashboard</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MetricCard
          icon={<Truck size={24} />}
          count={delivering}
          label="In Delivery"
          href="/orders?status=delivering"
          loading={loading}
        />
        <MetricCard
          icon={<Zap size={24} />}
          count={awaitingGeneration}
          label="Awaiting Generation"
          href="/orders?status=paid"
          loading={loading}
        />
        <MetricCard
          icon={<CreditCard size={24} />}
          count={activeSubscriptions}
          label="Active Subscriptions"
          href="/subscribers"
          loading={loading}
        />
        <MetricCard
          icon={<TrendingUp size={24} />}
          count={ordersThisMonth}
          label="Orders This Month"
          href="/orders"
          loading={loading}
        />
      </div>
    </div>
  );
}
